import { map, intersection } from 'lodash';
import moment from 'moment';

import { IUser } from '../store/UserSlice';
import { IRolePermissions } from '../store/V2/RolesSlice';
import { ICourseTutor } from '../store/V2/CourseTutorsSlice';
import { ITenant } from '../store/V2/TenantsSlice';
import { ITutorRole, ITutorRolePermissions } from '../store/V2/TutorRolesSlice';

export const checkUserRolePermissionOnly = (data: {
  authUser?: IUser;
  permissions: IRolePermissions;
}) => {
  const { authUser, permissions } = data;

  let hasRolePermission = false;

  if (authUser && permissions && authUser.role && authUser.role.permissions) {
    map(permissions, (permission, module) => {
      if (
        authUser.role &&
        authUser.role.permissions &&
        authUser.role.permissions[module] &&
        intersection(authUser.role.permissions[module], permission).length > 0
      ) {
        hasRolePermission = true;
      }
    });
  }

  return hasRolePermission;
};

export const checkTenantPermissionOnly = (data: {
  tenant?: ITenant;
  permissions: IRolePermissions;
}) => {
  const { tenant, permissions } = data;

  let hasTenantPermission = false;

  if (tenant && permissions && tenant.permissions) {
    map(permissions, (permission, module) => {
      if (
        tenant.permissions &&
        tenant.permissions[module] &&
        intersection(tenant.permissions[module], permission).length > 0
      ) {
        hasTenantPermission = true;
      }
    });
  }

  return hasTenantPermission;
};

export const checkUserPermission = (data: {
  tenant?: ITenant;
  authUser?: IUser;
  permissions: IRolePermissions;
}): boolean => {
  const { tenant, authUser, permissions } = data;

  let allPermissionsExist = false;

  map(permissions, (permission, module) => {
    const hasUserPermission =
      authUser?.role?.permissions?.[module] &&
      intersection(authUser.role.permissions[module], permission).length > 0;

    const hasTenantPermission =
      tenant?.permissions?.[module] &&
      intersection(tenant.permissions[module], permission).length > 0;

    if (hasUserPermission && hasTenantPermission) {
      allPermissionsExist = true;
    }
  });

  return allPermissionsExist;
};

export const checkIsUser = (authUser: IUser | undefined): boolean => {
  if (authUser && authUser.role && authUser.role.type === 'user') {
    return true;
  }

  return false;
};

export const isCourseTutorEligibleForClass = (
  courseTutor: ICourseTutor,
  classDate: string,
  classDurationMinutes: number
) => {
  let isEligible = false;

  if (courseTutor.status === 'ENABLE') {
    if (!courseTutor.startDate && !courseTutor.endDate) {
      isEligible = true;
    } else {
      const dateTime = moment(classDate);
      const classEndTime = dateTime
        .clone()
        .add(classDurationMinutes, 'minutes');

      if (courseTutor.startDate && courseTutor.endDate) {
        const startDate = moment(courseTutor.startDate).startOf('day');
        const endDate = moment(courseTutor.endDate).endOf('day');

        if (
          dateTime >= startDate &&
          dateTime <= endDate &&
          classEndTime <= endDate
        ) {
          isEligible = true;
        }
      } else if (courseTutor.startDate) {
        const startDate = moment(courseTutor.startDate).startOf('day');

        if (dateTime.isSameOrAfter(startDate)) {
          isEligible = true;
        }
      } else if (courseTutor.endDate) {
        const endDate = moment(courseTutor.endDate).endOf('day');

        if (
          dateTime.isSameOrBefore(endDate) &&
          classEndTime.isSameOrBefore(endDate)
        ) {
          isEligible = true;
        }
      }
    }
  }

  return isEligible;
};

export const checkCourseTutorRolePermission = (data: {
  courseTutorRole?: ITutorRole;
  permissions: ITutorRolePermissions;
}): boolean => {
  const { courseTutorRole, permissions } = data;

  if (!courseTutorRole || !permissions) {
    return false;
  }

  let hasRolePermission = false;

  map(permissions, (permission, module) => {
    if (
      courseTutorRole.permissions &&
      courseTutorRole.permissions[module] &&
      intersection(courseTutorRole.permissions[module], permission).length > 0
    ) {
      hasRolePermission = true;
    }
  });

  return hasRolePermission;
};
